import { useMutation } from '@apollo/client';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { CREATE_ONBOARDING } from '@retainerclub/customer-api';
import { useAuth, useReferral } from '@retainerclub/hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { NavLink, useNavigate, useSearchParams } from 'react-router';
import { CONSENT_VERSION } from '../../AccountConsent/ConsentCopy';
import { DISCLOSURE_VERSION, splitTiers } from '../../SubscriptionDisclosure';
import { initialValues, schema } from './helpers';
import OnboardingFormHeader from './OnboardingFormHeader';
import OnboardingFormInner from './OnboardingFormInner';

const REFERRAL_ALREADY_CLAIMED_MESSAGE =
  'This referral link has already been used. To update your information, sign in and go to your account dashboard.';

const REFERRAL_UNAVAILABLE_MESSAGE =
  'This referral link is no longer active. Please contact your dental provider for an updated invitation.';

const ALLOWED_REFERRAL_STATES = [
  'sent',
  'overdue1',
  'overdue2',
  'overdue3',
  'needs_attention',
];

function OnboardingForm() {
  const [urlSearchParams] = useSearchParams();
  const rId = urlSearchParams.get('rId');
  const { referral, partnerLocation, rError, pError, refetch } = useReferral();
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();

  const [mutationErrors, setMutationErrors] = useState(null);

  useEffect(() => {
    if (rId) {
      refetch();
    }
  }, [rId, refetch]);

  const isReferralAlreadyClaimed =
    referral?.consumedAt != null ||
    referral?.consumed_at != null ||
    referral?.state === 'completed';

  const isReferralUnavailable =
    referral != null &&
    !isReferralAlreadyClaimed &&
    !ALLOWED_REFERRAL_STATES.includes(referral?.state);

  const [create, { error, loading }] = useMutation(CREATE_ONBOARDING);

  const memoizedInitialValues = useMemo(
    () => initialValues({ referral, partnerLocation }),
    [referral, partnerLocation],
  );

  const {
    register,
    handleSubmit,
    trigger,
    formState,
    control,
    getValues,
    setValue,
    getFieldState,
    ...rest
  } = useForm({
    resolver: yupResolver(schema({ referral })),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  async function submitRecord(values) {
    if (isReferralAlreadyClaimed || isReferralUnavailable) {
      return;
    }

    try {
      const { subscriptionAcknowledgments, consentSelectAll, ...rest } =
        values;
      // The "Additional plans" link only exists for an item with tiers
      // behind it; otherwise the ledger records null rather than "not opened".
      const disclosure = referral?.subscriptionDisclosure;
      const additionalOffered = (key) => {
        const item = disclosure?.items?.find((entry) => entry.key === key);
        return (
          splitTiers({ product: item?.product, tiers: disclosure?.tiers })
            .additional.length > 0
        );
      };
      const params = {
        variables: {
          input: {
            input: {
              ...rest,
              consentVersion: CONSENT_VERSION,
              consentSelectAll: consentSelectAll === true,
              subscriptionAcknowledgments: (
                subscriptionAcknowledgments || []
              ).map((acknowledgment) => ({
                key: acknowledgment.key,
                agreed: acknowledgment.agreed === true,
                disclosureVersion: DISCLOSURE_VERSION,
                selectedSubscriptionProductId:
                  acknowledgment.selectedSubscriptionProductId || null,
                lowerTiersExpanded: additionalOffered(acknowledgment.key)
                  ? acknowledgment.lowerTiersExpanded === true
                  : null,
              })),
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data, errors } = await create(params);

      if (data?.errors?.length > 0 || errors?.length > 0) {
        const err = data?.errors?.length > 0 ? data?.errors : errors;

        setMutationErrors(err);
      } else if (data?.createOnboarding?.errorMessages?.length > 0) {
        setMutationErrors(data?.createOnboarding?.errorMessages);
      } else if (currentUser) {
        navigate('/', {});
      } else {
        await login({
          phone: values.userAttributes.phone,
          password: values.userAttributes.password,
          skipNavigate: true,
        });

        navigate('/', {});
      }
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      trigger={trigger}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
      getFieldState={getFieldState}
    >
      <form onSubmit={handleSubmit(submitRecord)} className="" noValidate>
        <div className="flex flex-col gap-y-4 sm:gap-y-6">
          <OnboardingFormHeader />
          {isReferralAlreadyClaimed && (
            <div
              className="flex items-center gap-3 rounded-lg border border-red-500 bg-red-50 p-4 font-medium text-base text-red-800"
              role="alert"
            >
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
              <p className="text-center">{REFERRAL_ALREADY_CLAIMED_MESSAGE}</p>
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
            </div>
          )}
          {isReferralUnavailable && (
            <div
              className="flex items-center gap-3 rounded-lg border border-red-500 bg-red-50 p-4 font-medium text-base text-red-800"
              role="alert"
            >
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
              <p className="text-center">{REFERRAL_UNAVAILABLE_MESSAGE}</p>
              <ExclamationTriangleIcon
                aria-hidden
                className="size-8 shrink-0 text-red-600"
              />
            </div>
          )}
          <div className="relative z-10 mx-auto flex w-full max-w-full flex-col gap-y-8 bg-rcgray-50 p-8 md:max-w-5xl lg:rounded-lg lg:border lg:shadow-2xl">
            <div>
              <p className="font-medium text-base">
                Already have a Retainer Club Account?&nbsp;
                <NavLink
                  to="/signin"
                  className="font-bold text-rcprimary-400 underline hover:text-black"
                >
                  Sign In Here
                </NavLink>
              </p>
            </div>
            {(referral !== null && referral !== undefined) ||
            (partnerLocation !== null && partnerLocation !== undefined) ? (
              <OnboardingFormInner
                error={error}
                isReferralAlreadyClaimed={isReferralAlreadyClaimed}
                isReferralUnavailable={isReferralUnavailable}
                loading={loading}
                mutationErrors={mutationErrors}
                pError={pError}
                rError={rError}
              />
            ) : (
              <div className="mt-20">
                <h1 className="text-center text-lg">
                  We&apos;re looking for your information. Give us a moment
                  while we load your provider&apos;s details.
                  <br />
                  Stuck here? Go back to the home page.&nbsp;
                  <a
                    href="https://retainerclub.com"
                    className="font-normal text-gray-9 text-sm leading-6 hover:text-rcprimary-400 hover:underline"
                  >
                    retainerclub.com
                  </a>
                </h1>
              </div>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

export default OnboardingForm;
